import { z } from 'zod';
import type { BlockDefinition } from '../types';
import { sanitizedHtmlProps } from '../lib/sanitize';

export const faqSchema = z.object({
  eyebrow: z.string().max(80).optional(),
  title: z.string().min(1).max(200),
  subtitle: z.string().max(500).optional(),
  align: z.enum(['left', 'center']).default('left'),
  items: z
    .array(
      z.object({
        question: z.string().min(1).max(300),
        answer: z.string().min(1).max(3000),
      }),
    )
    .min(1)
    .max(30),
});

export type FaqData = z.infer<typeof faqSchema>;

export function FaqRenderer({ eyebrow, title, subtitle, items }: FaqData) {
  const safeItems = Array.isArray(items) ? items : [];
  return (
    <section className="v5-section v5-faq-section">
      <div className="container">
        <div className="v5-faq-grid">
          <aside className="v5-faq-aside">
            {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
            <h2 className="v5-faq-h2">{title}</h2>
            {subtitle ? <p className="v5-feat-sub">{subtitle}</p> : null}
            <a className="v5-faq-help" href="/contacto#agendar">
              <span className="v5-faq-help-icon" aria-hidden>?</span>
              <span className="v5-faq-help-text">
                <strong>¿No encuentras tu respuesta?</strong>
                Habla con un asesor sénior →
              </span>
            </a>
          </aside>
          <div className="v5-faq-list">
            {safeItems.map((item, i) => (
              <details key={i} className="v5-faq-item">
                <summary className="v5-faq-q">
                  <span className="v5-faq-idx" aria-hidden>{String(i + 1).padStart(2, '0')}</span>
                  <span className="v5-faq-q-text">{item.question}</span>
                </summary>
                <div
                  className="v5-faq-a"
                  dangerouslySetInnerHTML={sanitizedHtmlProps(item.answer)}
                />
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export const faqBlock: BlockDefinition<FaqData> = {
  meta: {
    type: 'faq',
    label: 'FAQ (v5)',
    description: 'Acordeón de preguntas frecuentes con details/summary nativos. Respuestas admiten HTML simple (strong, em, a).',
    category: 'content',
    icon: 'help-circle',
  },
  schema: faqSchema,
  defaults: {
    eyebrow: '● Preguntas frecuentes',
    title: 'Resolvemos tus dudas',
    align: 'left',
    items: [
      {
        question: '¿Cuánto cobráis por vender mi empresa?',
        answer:
          '<strong>Honorarios a éxito</strong> en la mayoría de mandatos: solo cobramos si vendemos. El % se acuerda al inicio según tamaño y complejidad.',
      },
      {
        question: '¿Cuánto tarda vender una empresa?',
        answer: 'Plazo medio: <strong>6,4 meses</strong> desde firma del mandato hasta cierre.',
      },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 80 },
    { name: 'title', label: 'Título', type: 'text', required: true, max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 500 },
    {
      name: 'align',
      label: 'Alineación cabecera',
      type: 'select',
      options: [
        { label: 'Izquierda', value: 'left' },
        { label: 'Centrado', value: 'center' },
      ],
    },
    {
      name: 'items',
      label: 'Preguntas',
      type: 'array',
      max: 30,
      itemFields: [
        { name: 'question', label: 'Pregunta', type: 'text', required: true, max: 300 },
        { name: 'answer', label: 'Respuesta (HTML simple permitido)', type: 'richtext', required: true },
      ],
    },
  ],
  Renderer: FaqRenderer,
};
